import React, { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Alert, CircularProgress, Select, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { auth } from '../../middleware/auth0.service';
import { ShopBarber } from '../../types/shop';
import { addBarberToShop } from '../../middleware/registration_service/updateRequest';
import { Auth0Error } from 'auth0-js';
import { useProfileData } from '../../context/ProfileDataContext';

// Add validation schema
const schema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
  title: Yup.string().required('Title is required'),
});

interface AddNewBarberProps {
  showOpen: boolean;
  /** Notifies the parent that the dialog has been dismissed/closed so it
   * can keep its own `open` state in sync (fixes the dialog requiring a
   * double toggle to reopen after being closed). */
  onClose?: () => void;
}

export default function AddNewBarber({ showOpen, onClose }: AddNewBarberProps) {
  const [open, setOpen] = useState(showOpen);
  const [loading, setLoading] = useState(false);
  const [showError, setError] = useState({
    error: false,
    value: '',
  });
  const [success, setSuccess] = useState(false);
  const { shopId, refreshShop } = useProfileData();

  const handleClose = () => {
    setOpen(false);
    setSuccess(false);
    setError({ error: false, value: '' });
    formik.resetForm();
    onClose?.();
  };

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      title: '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true);
      setError({ error: false, value: '' });

      auth.signup(
        {
          email: values.email,
          password: 'Password123#',
          connection: 'Username-Password-Authentication',
          userMetadata: {
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phone,
            title: values.title,
          },
        },
        async function (error: Auth0Error | null, result: any) {
          if (error) {
            setLoading(false);
            setError({ error: true, value: error.code || 'Signup failed' });
            return false;
          }

          const shopBarber: ShopBarber = {
            userId: result.Id,
            title: values.title,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: values.phone,
            shopId: shopId,
          };

          try {
            await addBarberToShop(shopBarber, shopId);
            await refreshShop();
            setSuccess(true);
            setTimeout(() => {
              handleClose();
            }, 2000);
          } catch (err) {
            setError({ error: true, value: 'Failed to add barber to shop' });
          } finally {
            setLoading(false);
          }
        }
      );
    },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '90%',
          maxWidth: '600px',
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            fontFamily: 'GFS Didot',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <PersonAddAlt1Icon sx={{ color: '#dd8709' }} />
          Add New Barber
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {showError.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {showError.value === 'invalid_signup'
                ? 'Your email already exists. Please login with your email'
                : showError.value === 'invalid_password'
                ? 'Your password is not strong enough'
                : showError.value}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Barber Successfully Added!!!
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="firstName"
                fullWidth
                id="firstName"
                label="First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="lastName"
                label="Last Name"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="phone"
                label="Phone Number"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>
            <Grid item xs={12}>
              <Select
                fullWidth
                id="title"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                displayEmpty
                error={formik.touched.title && Boolean(formik.errors.title)}
              >
                <MenuItem value="">
                  <em>Select Title</em>
                </MenuItem>
                <MenuItem value="Mr">Mr</MenuItem>
                <MenuItem value="Mrs">Mrs</MenuItem>
                <MenuItem value="Miss">Miss</MenuItem>
                <MenuItem value="Dr">Dr</MenuItem>
                <MenuItem value="Prof">Prof</MenuItem>
              </Select>
              {formik.touched.title && formik.errors.title && (
                <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                  {formik.errors.title}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            className="buttonHover"
            type="submit"
            disabled={loading}
            fullWidth
            variant="contained"
            sx={{
              bgcolor: '#dd8709',
              color: '#fff',
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              p: 1,
              width: '100%',
              '&:hover': { bgcolor: '#b86e08' },
              '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Add New Barber'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
